import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from './CrmCustomers';

export const GROUPS = [
  ['active', 'Active', 'badge-approved'],
  ['slowing', 'Slowing', 'badge-pending'],
  ['lapsed', 'Lapsed', 'badge-declined'],
  ['new', 'New', 'badge-holiday'],
  ['inactive', 'Inactive', 'badge-cancelled'],
  ['no_sales', 'No sales yet', 'badge-cancelled'],
];
export const groupLabel = (g) => (GROUPS.find(x => x[0] === g) || [g, g])[1];
export const groupBadge = (g) => (GROUPS.find(x => x[0] === g) || [g, g, 'badge-cancelled'])[2];
export const money = (n) => (n === null || n === undefined ? '-' : `£${Math.round(n).toLocaleString('en-GB')}`);
export const pct = (n) => (n === null || n === undefined ? '-' : `${n > 0 ? '+' : ''}${n}%`);

const SETTING_FIELDS = [
  ['driver_hourly_rate', 'Driver pay per hour (£)', 0.01],
  ['driver_weekly_hours', 'Driver hours per week', 0.5],
  ['employer_ni_rate', 'Employer NI rate (0.15 = 15%)', 0.01],
  ['pension_rate', 'Employer pension rate (0.03 = 3%)', 0.01],
  ['avg_speed_mph', 'Average van speed (mph)', 1],
  ['stop_minutes', 'Minutes at each drop', 1],
  ['road_factor', 'Road miles per straight-line mile', 0.05],
  ['recent_weeks', 'Weeks counted as recent', 1],
  ['baseline_weeks', 'Weeks of usual spend before that', 1],
  ['change_threshold_pct', 'Flag a change of at least (%)', 1],
  ['change_min_usual_spend', 'Only flag when usual spend is at least (£)', 10],
];

const CrmInsights = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [grp, setGrp] = useState('');
  const [flag, setFlag] = useState('');
  const [sort, setSort] = useState('score');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [depots, setDepots] = useState([]);
  const [noVan, setNoVan] = useState('');
  const [channels, setChannels] = useState('');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(() => api.get('/api/insights/summary').then(r => setSummary(r.data)).catch(() => setError('Failed to load insights')), []);
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ sort });
      if (grp) p.set('grp', grp);
      if (flag) p.set('flag', flag);
      const r = await api.get(`/api/insights/customers?${p}`);
      setRows(r.data);
    } catch { setError('Failed to load customers'); } finally { setLoading(false); }
  }, [grp, flag, sort]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const openSettings = () => {
    const s = summary.settings;
    const f = {};
    SETTING_FIELDS.forEach(([k]) => { f[k] = s[k]; });
    setForm(f);
    setDepots(Object.entries(s.depot_postcodes || {}).map(([d, p]) => ({ d, p })));
    setNoVan((s.no_van_accounts || []).join('\n'));
    setChannels((s.channel_accounts || []).join('\n'));
    setError(''); setModal('settings');
  };
  const saveSettings = async () => {
    setSaving(true); setError('');
    try {
      const body = { ...form, no_van_accounts: noVan.split('\n'), channel_accounts: channels.split('\n'), depot_postcodes: Object.fromEntries(depots.filter(x => x.d.trim()).map(x => [x.d.trim(), x.p])) };
      await api.put('/api/insights/settings', body);
      setModal(null); setSuccess('Settings saved and every customer worked out again');
      loadSummary(); loadRows();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); } finally { setSaving(false); }
  };

  const runImport = async (dryRun) => {
    if (!file) return setError('Choose the Transaction Report CSV first');
    setSaving(true); setError('');
    try {
      const data = new FormData(); data.append('file', file);
      const r = await api.post(`/api/insights/transactions${dryRun ? '?dry_run=true' : ''}`, data);
      setResult(r.data);
      if (!dryRun) { setSuccess(`Imported ${r.data.lines.toLocaleString('en-GB')} lines, ${formatDate(r.data.from)} to ${formatDate(r.data.to)}`); setTimeout(() => { loadSummary(); loadRows(); }, 3000); }
    } catch (err) { setError(err.response?.data?.error || 'Import failed'); } finally { setSaving(false); }
  };

  const openUnmatched = async () => {
    setError('');
    try { const r = await api.get('/api/insights/unmatched'); setUnmatched(r.data); setModal('unmatched'); } catch { setError('Failed to load'); }
  };

  const g = summary?.groups || {};
  const d = summary?.data || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Customer insights</h1>
          <p className="page-subtitle">
            {d.lines ? `From Autovolt sales, ${formatDate(d.from)} to ${formatDate(d.to)} (${(d.depots || []).join(', ')}). Worked out ${summary?.computed_at ? formatDate(summary.computed_at) : '-'}.` : 'No sales loaded yet.'}
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-outline" onClick={() => { setFile(null); setResult(null); setError(''); setModal('import'); }}>Import sales</button>
            <button className="btn btn-outline" onClick={openSettings} disabled={!summary}>Settings</button>
          </div>
        )}
      </div>

      {error && !modal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="stat-grid">
        {GROUPS.map(([k, label]) => (
          <button key={k} className={`stat-card ${grp === k ? 'red-accent' : 'blue-accent'}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setGrp(grp === k ? '' : k)}>
            <div className="stat-label">{label}</div>
            <div className="stat-value">{g[k] || 0}</div>
          </button>
        ))}
        <button className={`stat-card ${flag === 'down' ? 'red-accent' : 'blue-accent'}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setFlag(flag === 'down' ? '' : 'down')}>
          <div className="stat-label">Spend down</div>
          <div className="stat-value">{summary?.flags?.down || 0}</div>
        </button>
        <button className={`stat-card ${flag === 'up' ? 'red-accent' : 'blue-accent'}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setFlag(flag === 'up' ? '' : 'up')}>
          <div className="stat-label">Spend up</div>
          <div className="stat-value">{summary?.flags?.up || 0}</div>
        </button>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '8px' }}>
          <h3 className="card-title">
            {rows.length} customer{rows.length === 1 ? '' : 's'}{grp ? `, ${groupLabel(grp).toLowerCase()}` : ''}{flag ? `, spend ${flag}` : ''}
            {(grp || flag) && <button className="btn btn-outline btn-sm" style={{ marginLeft: '10px' }} onClick={() => { setGrp(''); setFlag(''); }}>Clear</button>}
          </h3>
          <select className="form-control" style={{ width: 'auto' }} value={sort} onChange={e => setSort(e.target.value)}>
            <option value="score">Sort by score</option>
            <option value="net">Sort by net contribution</option>
            <option value="sales">Sort by sales</option>
            <option value="change">Biggest drop first</option>
            <option value="last">Most recent order</option>
            <option value="name">Name</option>
          </select>
        </div>
        {loading ? <div className="loader">Loading...</div> : rows.length === 0 ? (
          <div className="empty-state"><h3>Nothing here</h3><p>{d.lines ? 'No customers match.' : 'An admin imports sales from Autovolt to start.'}</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Customer</th><th>Group</th><th>Last order</th><th>Last {summary?.settings?.recent_weeks || 8} wks</th>
                  <th>12m sales</th><th>12m margin</th><th>Delivery cost</th><th>Net</th><th>Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 500, maxWidth: '280px' }}>
                      {user?.can_use_crm ? <Link to={`/crm/customers/${r.id}`}>{r.name}</Link> : r.name}
                      {(r.ai_note || r.rule_note) && <div style={{ fontSize: '12px', color: 'var(--grey-mid)', fontWeight: 400, marginTop: '2px' }}>{r.ai_note || r.rule_note}</div>}
                    </td>
                    <td><span className={`badge ${groupBadge(r.grp)}`}>{groupLabel(r.grp)}</span></td>
                    <td style={{ color: 'var(--grey-mid)', whiteSpace: 'nowrap' }}>{r.last_order_on ? formatDate(r.last_order_on) : '-'}{r.days_since !== null && r.last_order_on && <div style={{ fontSize: '11px' }}>{r.days_since === 0 ? 'today' : `${r.days_since} day${r.days_since === 1 ? '' : 's'} ago`}{r.usual_gap_days ? `, usually every ${Math.round(r.usual_gap_days)}` : ''}</div>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{money(r.recent_spend)}{r.change_pct !== null && <div style={{ fontSize: '11px', color: r.flag === 'down' ? 'var(--red)' : r.flag === 'up' ? 'var(--success, #2d6a4f)' : 'var(--grey-mid)', fontWeight: r.flag ? 600 : 400 }}>{pct(r.change_pct)} on usual</div>}</td>
                    <td>{money(r.sales_12m)}</td>
                    <td>{money(r.margin_12m)}{r.sales_12m > 0 && <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{Math.round((r.margin_12m / r.sales_12m) * 100)}%</div>}</td>
                    <td>{money(r.delivery_cost_12m)}<div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{r.deliveries_12m} drops{r.deliveries_unknown_distance ? `, ${r.deliveries_unknown_distance} unknown` : ''}</div></td>
                    <td style={{ fontWeight: 600 }}>{money(r.net_12m)}</td>
                    <td>{r.is_channel ? <span className="badge badge-cancelled">Channel</span> : r.score === null ? '-' : <strong>{r.score}</strong>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '16px' }}>
        <div className="card-body" style={{ fontSize: '12px', color: 'var(--grey-mid)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--navy)' }}>How this is worked out.</strong> Groups follow each customer's own rhythm: the usual gap is the middle gap between their order days; Active is up to twice that, Slowing up to four times, then Lapsed, and Inactive after a year or when stopped in the ERP. Customers with fewer than three order days use 30, 90 and 365 days.
          {' '}Spend change compares the last {summary?.settings?.recent_weeks || 8} weeks with the same customer's usual rate over the {summary?.settings?.baseline_weeks || 26} weeks before, and is only flagged at {summary?.settings?.change_threshold_pct || 30}% or more on a usual spend of at least {money(summary?.settings?.change_min_usual_spend || 250)}.
          {' '}Margin is sale price less Autovolt's cost price. Delivery cost is the drive out from the depot to the delivery postcode (straight line times {summary?.settings?.road_factor || 1.25} for roads, at {summary?.settings?.avg_speed_mph || 25} mph) plus {summary?.settings?.stop_minutes || 10} minutes at the drop, at £{summary?.driver_hour_cost?.toFixed(2)} an hour: the National Living Wage of £{Number(summary?.settings?.driver_hourly_rate || 0).toFixed(2)} with employer NI and pension. Only the leg out counts, as vans run multi-drop routes. Collections, parcels sent from the depot and courier accounts carry no van cost. Fuel is not included until ABAX is connected{summary?.abax ? ' (ABAX credentials are present; trips are not read yet)' : ''}.
          {' '}Score is where a customer's net contribution (margin less delivery cost, 12 months) ranks among customers with sales, 0 to 100. Sales channels (eBay, the website) are shown but not scored until their own costs are set up.
          {' '}{summary?.ai ? 'Notes on changing customers are written by AI from the figures only; the flags themselves come from the rules above.' : 'Notes come from the figures; AI notes switch on when an Anthropic API key is set.'}
          {isAdmin && d.unmatched_names > 0 && <> <button className="btn btn-outline btn-sm" style={{ marginLeft: '6px' }} onClick={openUnmatched}>{d.unmatched_names} Autovolt name{d.unmatched_names === 1 ? '' : 's'} not matched to a customer</button></>}
        </div>
      </div>

      {modal === 'import' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Import sales from Autovolt</h3><button className="modal-close" onClick={() => setModal(null)}>&times;</button></div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ fontSize: '13px', color: 'var(--grey-mid)', marginTop: 0 }}>
                Autovolt, Reports, Transaction Report, as CSV. A file replaces whatever is already here for its depot and dates, so sending the same days twice never counts them twice. Customers are matched on their exact account name.
              </p>
              <div className="form-group"><label className="form-label">CSV file</label><input type="file" accept=".csv,text/csv" className="form-control" onChange={e => { setFile(e.target.files[0] || null); setResult(null); }} /></div>
              {result && (
                <div className={`alert ${result.dry_run ? 'alert-info' : 'alert-success'}`}>
                  <strong>{result.dry_run ? 'Preview, nothing saved yet' : 'Imported'}</strong><br />
                  {result.lines.toLocaleString('en-GB')} lines from {formatDate(result.from)} to {formatDate(result.to)} ({result.depots.join(', ')}). Replaces {result.replaced.toLocaleString('en-GB')} lines already held. {result.matched.toLocaleString('en-GB')} of {result.total.toLocaleString('en-GB')} matched to a customer; {result.unmatched_names} name{result.unmatched_names === 1 ? '' : 's'} not matched.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => runImport(true)} disabled={saving || !file}>Preview</button>
              <button className="btn btn-primary" onClick={() => runImport(false)} disabled={saving || !result?.dry_run}>{saving ? 'Working...' : 'Import'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'settings' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Insight settings</h3><button className="modal-close" onClick={() => setModal(null)}>&times;</button></div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 16px' }}>
                {SETTING_FIELDS.map(([k, label, step]) => (
                  <div className="form-group" key={k}>
                    <label className="form-label">{label}</label>
                    <input type="number" step={step} className="form-control" value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <h4 style={{ margin: '4px 0 8px', fontSize: '14px' }}>Depot postcodes</h4>
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)', marginTop: 0 }}>Where each depot's vans start from. The depot name must match Autovolt's exactly.</p>
              {depots.map((x, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input className="form-control" value={x.d} onChange={e => setDepots(ds => ds.map((y, j) => (j === i ? { ...y, d: e.target.value } : y)))} placeholder="Depot name" />
                  <input className="form-control" style={{ maxWidth: '140px' }} value={x.p} onChange={e => setDepots(ds => ds.map((y, j) => (j === i ? { ...y, p: e.target.value } : y)))} placeholder="Postcode" />
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => setDepots(ds => [...ds, { d: '', p: '' }])}>+ Add depot</button>
              <h4 style={{ margin: '16px 0 8px', fontSize: '14px' }}>Accounts not delivered by our vans</h4>
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)', marginTop: 0 }}>One account name per line, exactly as in the CRM. Their orders go by courier or post, so no van cost is counted. Deliveries to a depot's own postcode (collections, eBay parcels) are never counted either.</p>
              <textarea className="form-control" rows={4} value={noVan} onChange={e => setNoVan(e.target.value)} />
              <h4 style={{ margin: '16px 0 8px', fontSize: '14px' }}>Sales channels, not scored</h4>
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)', marginTop: 0 }}>One account name per line. Whole channels such as eBay and the website, rather than customers. Their figures still show, but they get no score until their own costs (the online team, postage per order) are set up.</p>
              <textarea className="form-control" rows={3} value={channels} onChange={e => setChannels(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save and work out again'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'unmatched' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Names not matched</h3><button className="modal-close" onClick={() => setModal(null)}>&times;</button></div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: 'var(--grey-mid)', marginTop: 0 }}>Autovolt customer names with no CRM account of exactly that name. Their sales are held but not counted against anyone. Rename the account (or run the ERP import) and they match on the next import.</p>
              <div className="table-wrap"><table><thead><tr><th>Name in Autovolt</th><th>Sales</th><th>Last</th></tr></thead><tbody>
                {unmatched.map(u => <tr key={u.contact_name}><td>{u.contact_name}</td><td>{money(u.sales)}</td><td>{formatDate(u.last_on)}</td></tr>)}
              </tbody></table></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmInsights;
